import { useRef, useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useSnapshot } from 'valtio'
import { chatAction, configState } from '@/store'

export const TemplateCardList = () => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)
  const { bannerData } = useSnapshot(configState)

  const handleSend = (item: any) => {
    chatAction.onUserSubmit({
      text: item.text,
      pic: item.pic,
    })
  }

  const checkScroll = () => {
    if (!scrollRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
    // Allow a small tolerance (e.g. 1px) for floating point calculations
    setShowLeftArrow(scrollLeft > 1)
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1)
  }

  useEffect(() => {
    checkScroll()
    // Check again after a short delay to ensure layout is settled
    const timer = setTimeout(checkScroll, 100)
    window.addEventListener('resize', checkScroll)
    return () => {
      window.removeEventListener('resize', checkScroll)
      clearTimeout(timer)
    }
  }, [])

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return
    const scrollAmount = 212 * 3 // card width (200) + gap (12)
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  return (
    <div className="relative w-full overflow-hidden">
      <div
        className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center bg-gradient-to-r from-slate-50 to-transparent z-10 transition-opacity duration-300 ${showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <button
          onClick={() => {
            scroll('left')
          }}
          className="cursor-pointer size-7 flex justify-center items-center bg-white rounded-full border border-border-default shadow-[0px_0px_8px_0px_rgba(0,0,0,0.05)]"
        >
          <ChevronLeft className="size-4 text-content-second" />
        </button>
      </div>

      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-3 overflow-x-auto px-5 scrollbar-none"
      >
        {(bannerData?.features?.thinking_w_images || [])?.map(
          (item: any, index: number) => (
            <div
              key={index}
              className="flex-shrink-0 w-[200px] h-[212px] bg-[#F2F9FF] rounded-xl overflow-hidden cursor-pointer select-none"
              onClick={() => {
                handleSend(item)
              }}
            >
              {/* Image Area */}
              <div className="h-[120px] w-full flex items-center justify-center bg-fill-0 overflow-hidden">
                <img
                  src={item.pic}
                  className="w-full h-full object-cover"
                  alt=""
                />
              </div>
              {/* Content Area */}
              <div className="p-4 flex flex-col gap-2">
                <div className="font-medium text-sm text-content-primary line-clamp-1">
                  {item.title}
                </div>
                <div className="text-xs text-content-auxiliary line-clamp-2">
                  {item.text}
                </div>
              </div>
            </div>
          ),
        )}
      </div>

      <div
        className={`absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center bg-gradient-to-l from-slate-50 to-transparent z-10 transition-opacity duration-300 ${showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div
          onClick={() => {
            scroll('right')
          }}
          className="cursor-pointer size-7 flex justify-center items-center bg-white rounded-full border border-border-default shadow-[0px_0px_8px_0px_rgba(0,0,0,0.05)]"
        >
          <ChevronRight className="size-4 text-content-second" />
        </div>
      </div>
    </div>
  )
}
